import { useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { VehicleSelect } from '@/components_deprecated/Select'
import { Module } from '@/modules/companies/consts'
import { AllVehicleExpirationDates } from '@/modules/vehicles/fleet/components/AllVehicleExpirationDates'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Button } from '../../../../components_deprecated'
import type { IdVariable } from '../../../../types/graphql'
import { useOnErrorAuto } from '../../../../utils'
import type {
  AllFormTemplatesQuery,
  FormField,
  FormTemplateQuery,
} from '../../../formcreator'
import { CreateForm } from '../../../formcreator'
import {
  ALL_FORM_TEMPLATES_QUERY,
  FORM_TEMPLATE_QUERY,
} from '../../../formcreator/queries'
import { useVehicleExpirationTypeMutations } from '../../../vehicles/fleet/mutations.hooks'
import type { VehicleExpirationTypeForm } from '../components'
import {
  OldAddButton,
  Header,
  InfoBox,
  OldSection,
  SettingsWrapper,
} from '../components'
import { AddVehicleExpirationTypeModal } from '../components/AddVehicleExpirationTypeModal'
import { ModuleResponsibleUsers } from '../components/ModuleResponsibleUsers'

const StyledHeader = styled(Header)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
`

const StyledVehicleSelect = styled(VehicleSelect)`
  max-width: 400px;
`

const StyledAddCategoryNameBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

interface VehicleFleetSettingsProps {}

export const VehicleFleetSettings: React.FC<VehicleFleetSettingsProps> = () => {
  const translations = useTranslate({
    info: 'settings.info.vehicles',
    vehicleExpirations: 'vehicles.expiration-dates',
    documentsAndExpirations: 'common.documents-and-expirations',
    type: 'form.type-material',
    createForm: 'common.create-form',
    create: 'common.create-alt',
    editForm: 'form.edit-form',
    close: 'common.close',
    addExpirationType: 'settings.add-vehicle-expiration-type',
  })
  const onErrorAuto = useOnErrorAuto()
  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [isCreate, setIsCreate] = useState<boolean>(false)
  const { data: dataForm, loading } = useQuery<
    AllFormTemplatesQuery,
    { q: string; archived: boolean; orderBy: string; type: string }
  >(ALL_FORM_TEMPLATES_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      q: '',
      archived: false,
      orderBy: 'name',
      type: 'material',
    },
    onCompleted(data) {
      if (!data) return
    },
    onError: onErrorAuto(),
  })

  const formData = dataForm?.allFormTemplates.edges[0]?.node

  const { data } = useQuery<FormTemplateQuery, IdVariable>(
    FORM_TEMPLATE_QUERY,
    {
      skip: !formData?.id,
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: { id: formData?.id ?? '' },
      onError: onErrorAuto(),
    }
  )

  const form = () => {
    if (!data) return null

    const sortableData = [...data.formTemplate.formFields.edges]
    const sortedData = sortableData.sort(
      (a, b) => (a.node.order ?? 0) - (b.node.order ?? 0)
    )

    const fields: FormField[] = sortedData.map(({ node: field }) => ({
      id: field.id,
      type: field.fieldType,
      name: field.name,
      description: field.description,

      required: field.required,
      minimum: field.minimum ?? 0,
      maximum: field.maximum ?? 10,
      minimumEnabled: field.fieldType === 'NUMBER' && field.minimum !== null,
      maximumEnabled: field.fieldType === 'NUMBER' && field.maximum !== null,
    }))

    return {
      id: data.formTemplate.id,
      name: data.formTemplate.name,
      ingress: data.formTemplate.ingress,
      archived: data.formTemplate.archivedAt !== null,
      menuShortcut: data.formTemplate.menuShortcut,
      type: data.formTemplate?.type,
      fields,
    }
  }

  const addPrompt = usePrompt()
  const { createVehicleExpirationType } = useVehicleExpirationTypeMutations()

  const handleCreate = async () => {
    const { data } = await addPrompt<VehicleExpirationTypeForm | null>(
      resolve => <AddVehicleExpirationTypeModal onSubmit={resolve} />
    )

    if (!data) return

    await createVehicleExpirationType({
      variables: {
        input: {
          name: data.categoryName,
        },
      },
    })
  }

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <InfoBox>
        <p>{translations.info}</p>
      </InfoBox>

      <OldSection>
        <StyledHeader>
          <h2>{translations.type}</h2>
          <Button disabled={loading} onClick={() => setIsCreate(prev => !prev)}>
            {isCreate
              ? translations.close
              : formData && form()
              ? translations.editForm
              : translations.createForm}
          </Button>
        </StyledHeader>
      </OldSection>
      {isCreate && (
        <CreateForm
          customStyles={{
            padding: '0px',
            width: 'auto',
          }}
          headerStyles={{
            padding: '28px',
            borderRadius: '12px',
            background: 'white',
          }}
          material
          initialData={(formData && form()) || undefined}
          onChange={() => setIsCreate(false)}
        />
      )}

      <StyledAddCategoryNameBlock>
        <OldAddButton
          addText={translations.addExpirationType}
          adding={false}
          onClick={handleCreate}
        />
      </StyledAddCategoryNameBlock>
      <ModuleResponsibleUsers module={Module.VEHICLES} />

      <OldSection>
        <StyledHeader>
          <h2>{translations.documentsAndExpirations}</h2>
          <StyledVehicleSelect
            onVehicleSelectedCallback={vehicle =>
              vehicle ? setVehicleId(vehicle.id) : setVehicleId(null)
            }
            nullable
          />
        </StyledHeader>
        <AllVehicleExpirationDates vehicleId={vehicleId} />
      </OldSection>
    </SettingsWrapper>
  )
}
